import React from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';

// Note: 'amber' and 'green' here are friendly aliases for the warning/success
// tokens, not the literal amber/green color scales — 'amberTone'/'greenTone'
// exist separately below for callers that need those literal scales.
export type BoxedIconVariants =
  | 'green'
  | 'blue'
  | 'blueDark'
  | 'amber'
  | 'rose'
  | 'violet'
  | 'teal'
  | 'amberTone'
  | 'greenTone';

export interface BoxedIconProps {
  icon: React.ElementType<SvgIconProps> | React.ReactNode;
  variant: BoxedIconVariants;
  size?: number;
  padding?: number | string;
}

export default function BoxedIcon({
  icon: Icon,
  variant,
  size = 44,
  padding = 1,
}: BoxedIconProps) {
  const theme = useTheme();
  const variantStyles: Record<
    BoxedIconVariants,
    {
      backgroundColor: string;
      iconColor: string;
    }
  > = {
    green: {
      backgroundColor: theme.palette.semantic.surface.success,
      iconColor: theme.palette.semantic.icon.success,
    },
    blue: {
      backgroundColor: theme.palette.semantic.surface.info,
      iconColor: theme.palette.semantic.icon.blue,
    },
    blueDark: {
      backgroundColor: theme.palette.semantic.surface.info,
      iconColor: theme.palette.semantic.icon.blueDark,
    },
    amber: {
      backgroundColor: theme.palette.semantic.surface.warning,
      iconColor: theme.palette.semantic.icon.warning,
    },
    rose: {
      backgroundColor: theme.palette.semantic.surface.error,
      iconColor: theme.palette.semantic.icon.error,
    },
    violet: {
      backgroundColor: theme.palette.semantic.surface.violet,
      iconColor: theme.palette.semantic.icon.violet,
    },
    teal: {
      backgroundColor: theme.palette.semantic.surface.teal,
      iconColor: theme.palette.semantic.icon.teal,
    },
    amberTone: {
      backgroundColor: theme.palette.semantic.surface.amber,
      iconColor: theme.palette.semantic.icon.amber,
    },
    greenTone: {
      backgroundColor: theme.palette.semantic.surface.green,
      iconColor: theme.palette.semantic.icon.green,
    },
  };

  const colors = variantStyles[variant];

  if (!Icon) {
    return null;
  }

  let renderedIcon;

  if (React.isValidElement(Icon)) {
    // Check if the element is a native HTML tag (e.g., <img>, <span>)
    if (typeof Icon.type === 'string') {
      // Cast the icon so TypeScript knows it can have standard HTML attributes
      const htmlIcon = Icon as React.ReactElement<
        React.HTMLAttributes<HTMLElement>
      >;

      renderedIcon = React.cloneElement(htmlIcon, {
        style: {
          color: colors.iconColor,
          width: 24,
          height: 24,
          ...(htmlIcon.props.style || {}),
        },
      });
    } else {
      // It's a React component (like an MUI SvgIcon), safe to use `sx`
      renderedIcon = React.cloneElement(
        Icon as React.ReactElement<{ sx?: object }>,
        {
          sx: {
            color: colors.iconColor,
            width: 24,
            height: 24,
            ...((Icon.props as SvgIconProps).sx ?? {}),
          },
        }
      );
    }
  } else {
    // If passed as an ElementType (e.g., icon={CheckCircle})
    renderedIcon = React.createElement(
      Icon as React.ElementType<SvgIconProps>,
      {
        sx: { color: colors.iconColor, width: 24, height: 24 },
      }
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        p: padding,
        borderRadius: 1,
        bgcolor: colors.backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {renderedIcon}
    </Box>
  );
}
